import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FullLayout from 'layout/FullLayout';
import { interactiveExhibitions } from './data';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

// ── Main page ─────────────────────────────────────────────────────────────────
const KinteractivePage = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(interactiveExhibitions[0].id);

  const active = interactiveExhibitions.find((e) => e.id === activeId);

  // Lock page scroll while on this page
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <FullLayout>
      <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 60px)' }}>
        {/* ── LEFT: Showcase ───────────────────────────────────────────── */}
        <div className="flex-1 relative overflow-hidden">
          {/* Crossfade images */}
          {interactiveExhibitions.map((exhibition) => {
            const isActive = activeId === exhibition.id;
            return (
              <div
                key={exhibition.id}
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: isActive ? 1 : 0 }}>
                <img
                  src={normalizeImageSrc(exhibition.mainImage)}
                  alt={exhibition.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
                  style={{ transform: isActive ? 'scale(1.03)' : 'scale(1.08)' }}
                />
                {/* Warm overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to top, rgba(30,15,5,0.92) 0%, rgba(30,15,5,0.35) 50%, rgba(0,0,0,0.05) 100%)',
                  }}
                />
              </div>
            );
          })}

          {/* Accent bar top */}
          <div
            className="absolute top-0 left-0 w-full h-[3px] z-10 transition-colors duration-500"
            style={{ backgroundColor: active?.accentColor }}
          />

          {/* Large order watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 transition-colors duration-500">
            <span
              className="text-[18rem] font-black leading-none select-none"
              style={{ color: active?.accentColor, opacity: 0.2 }}>
              {active?.order}
            </span>
          </div>

          {/* Bottom content */}
          <div className="absolute bottom-0 left-0 right-0 px-8 lg:px-14 pb-10 z-20">
            <span
              className="inline-block text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full text-white mb-4"
              style={{ backgroundColor: active?.accentColor }}>
              {active?.tag}
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-black text-white leading-none tracking-tight mb-2">
              {active?.name}
            </h2>
            <p className="text-white/50 text-sm font-medium mb-5 max-w-md leading-relaxed">
              {active?.subtitle}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {active?.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full text-white/60"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}>
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={() => navigate(`/kinteractive/${activeId}`)}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-black text-white transition-all duration-200 hover:opacity-90 active:scale-95"
              style={{ backgroundColor: active?.accentColor }}>
              Explore Exhibition
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>

          {/* Mobile dot nav */}
          <div className="md:hidden absolute top-5 right-5 z-20 flex flex-col gap-2">
            {interactiveExhibitions.map((e) => (
              <button
                key={e.id}
                onClick={() => setActiveId(e.id)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: '6px',
                  height: activeId === e.id ? '22px' : '6px',
                  backgroundColor: activeId === e.id ? e.accentColor : 'rgba(255,255,255,0.35)',
                }}
              />
            ))}
          </div>
        </div>

        {/* ── RIGHT: Cream panel with cards ────────────────────────────── */}
        <div className="hidden md:flex w-[340px] lg:w-[400px] flex-shrink-0 bg-[#fcf2e9] border-l border-[#e8d4bc] flex-col">
          {/* Header */}
          <div className="px-6 lg:px-8 pt-7 pb-6 border-b border-[#e8d4bc]">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-px w-5 bg-secondary-main" />
              <span className="text-[9px] font-black uppercase tracking-[0.4em] text-secondary-main">
                Play & Learn
              </span>
            </div>
            <h1 className="text-[1.15rem] font-black text-kin-textdark leading-none tracking-tight">
              KiN<span className="text-secondary-main">TERACTIVE</span>
            </h1>
            <p className="text-[9px] text-kin-text/55 mt-2 leading-relaxed max-w-[220px]">
              Hands-on exhibitions for kids and families celebrating Indonesia's culture and
              creativity.
            </p>
          </div>

          {/* Exhibition cards */}
          <div className="flex-1 flex flex-col justify-center gap-4 px-5 lg:px-6 py-6">
            {interactiveExhibitions.map((exhibition) => {
              const isActive = activeId === exhibition.id;
              return (
                <button
                  key={exhibition.id}
                  onClick={() => setActiveId(exhibition.id)}
                  className="group relative w-full text-left rounded-2xl overflow-hidden transition-all duration-300 focus:outline-none"
                  style={{
                    backgroundColor: isActive ? '#ffffff' : 'rgba(255,255,255,0.45)',
                    boxShadow: isActive
                      ? '0 4px 20px rgba(30,15,5,0.10)'
                      : '0 1px 4px rgba(30,15,5,0.04)',
                    border: isActive
                      ? `1.5px solid ${exhibition.accentColor}20`
                      : '1.5px solid rgba(232,212,188,0.5)',
                  }}>
                  {/* Active accent bar left */}
                  <div
                    className="absolute top-0 left-0 bottom-0 w-[3px] rounded-l-2xl transition-opacity duration-300"
                    style={{
                      backgroundColor: exhibition.accentColor,
                      opacity: isActive ? 1 : 0,
                    }}
                  />

                  <div className="flex items-stretch gap-0">
                    {/* Thumbnail */}
                    <div className="relative w-28 lg:w-32 flex-shrink-0 overflow-hidden">
                      <img
                        src={normalizeImageSrc(exhibition.mainImage)}
                        alt={exhibition.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        style={{ minHeight: '120px' }}
                      />
                      <div
                        className="absolute inset-0 transition-opacity duration-300"
                        style={{
                          backgroundColor: 'rgba(252,242,233,0.45)',
                          opacity: isActive ? 0 : 1,
                        }}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 px-4 py-4 min-w-0">
                      <p
                        className="text-[9px] font-black uppercase tracking-widest mb-1 transition-colors duration-300"
                        style={{
                          color: isActive ? exhibition.accentColor : 'rgba(144,133,114,0.5)',
                        }}>
                        {exhibition.order}
                      </p>
                      <h3
                        className="font-black leading-tight text-sm transition-colors duration-300 mb-1"
                        style={{ color: isActive ? '#1a0f05' : 'rgba(118,104,80,0.55)' }}>
                        {exhibition.name}
                      </h3>
                      <p
                        className="text-[10px] leading-relaxed line-clamp-2 transition-colors duration-300"
                        style={{ color: isActive ? '#908572' : 'rgba(144,133,114,0.4)' }}>
                        {exhibition.subtitle}
                      </p>

                      {isActive && (
                        <div
                          className="inline-flex items-center gap-1.5 mt-3 text-[10px] font-black"
                          style={{ color: exhibition.accentColor }}>
                          Open
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bottom stats */}
          <div className="border-t border-[#e8d4bc] px-6 lg:px-8 py-5 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="h-px w-3 bg-secondary-main/40" />
              <p className="text-[8px] font-bold uppercase tracking-wider text-kin-text/50">
                3 Interactive Exhibitions
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-px w-3 bg-secondary-main/40" />
              <p className="text-[8px] font-bold uppercase tracking-wider text-kin-text/50">
                In-house Design
              </p>
            </div>
          </div>
        </div>
      </div>
    </FullLayout>
  );
};

export default KinteractivePage;
